import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ANNUAL_PERCENTAGE_DISCLAIMER } from 'src/redux/modules/modals/modals.module';
import { Action, bindActionCreators, Dispatch } from 'redux';
import DeductionList from 'src/components/DeductionList/DeductionList';
import ExchangeToForm from 'src/components/Exchange/ExchangeToForm/ExchangeToForm';
import { Fund } from 'src/components/Exchange/types';
import ProgressStep from 'src/components/ProgressStep/ProgressStep';
import AppTitle from 'src/components/Shared/AppTitle/AppTitle';
import ErrorSlideIn from 'src/components/Shared/ErrorSlideIn/ErrorSlideIn';
import { withMaybeFragment } from 'src/hoc/withMaybeFragment';
import { moduleActions } from 'src/redux/modules';
import { UniqueIDs } from 'src/redux/modules/exchangeTransaction';
import {
  updateExchangeFederalWithholdingWithObject,
  updateUniqueIDs,
} from 'src/redux/modules/exchangeTransaction/actions';
import {
  getAutoExchangeAmountLessWithholding,
  getAutoExchangeFromEditFund,
  getAutoExchangeFromSummaryFund,
  getAutoExchangeHasEarlyWithdrawalPenalty,
  getAutoExchangeStartDate,
  getAutoExchangeToSummaryFund,
} from 'src/redux/modules/exchangeTransaction/autoExchangeTransaction.selectors';
import {
  getElectionAgreementUniqueIds,
  getExchangeFromIsValid,
  getExchangeFromSummaryFundList,
  getExchangeFundsWithInterestedPartyFlag,
  getExchangeIsTaxable,
  getExchangeToIsValid,
  getExchangeToSummaryFundList,
  getHasExchangeFromSweep,
} from 'src/redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import {
  getExchangeFrequency,
  getFederalWithholding,
  getFederalWithholdingDollars,
  getFrequencyString,
  getGrossUnit,
  getStateWithholding,
} from 'src/redux/modules/exchangeTransaction/selectors';
import { AutoExchangePayloadPreSubmit } from 'src/redux/modules/executeAutoExchange/types';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  IRA_WITHHOLDING_WARNING_MODAL,
} from 'src/redux/modules/modals/modals.module';
import { getProgressCurrentStep } from 'src/redux/modules/progress/selectors';
import {
  getMandatoryWithholdingStates,
  getNonMandatoryWithholdingStates,
  getUserStateWithholdingRules,
} from 'src/redux/modules/stateWithholding/selectors';
import { ApplicationState } from 'src/redux/modules/types';
import { getUserAddress } from 'src/redux/modules/user/PersonalInformation/personalInformation.selectors';
import {
  CodeToSelectedUnit,
  ProductTypes,
  SELECTED_UNIT_ENUM_OBJECT_MAP,
  UNIT,
  FederalTaxPercent,
  AGREEMENT_TYPES,
} from 'src/settings/settings';
import { FundSummary, Withholding } from 'src/types';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import { getPaperless } from 'src/utils/submission/submission-utility';
import { isAutoExchangeEdit } from 'src/utils/transactions-util';
import { conditionallyFormatValue, hasValue } from 'src/utils/utils';
import { WithholdingPanel } from '../../../Shared/WithholdingPanel';
import AutoExchangeFrequency from '../Frequency';
import AutoExchangeFromForm from '../FromForm';
import { FundSummaryDisplay } from '../FundSummaryDisplay';
import { AutoExchangeReview } from '../Review';
import { AutoExchangeSummary } from '../Summary';
import styles from './Steps.module.scss';
import { getRecurringStartDate } from 'src/redux/modules/shoppingCart/actions';
import {
  isCustomerWebViewEnabled,
  isEmbeddedInIFrame,
  isSalesforceRep,
} from 'src/redux/modules/authentication/authentication.module.selectors';
import If from '../../../Shared/If/If';
import '../../../Buy/BuyFlow/ExpandedFundList//MarketVolatilityNote.scss';
import StateIncomeTaxDisclosure from '../../../Sell/StateIncomeTaxDisclosure/StateIncomeTaxDisclosure';
import IraWithholdingWarning from '../../../Sell/IraWithholdingWarning/IraWithholdingWarning';
import CheckBox from 'src/components/Shared/Checkbox/Checkbox';
import StateWithholdingNonMandatory from 'src/components/Sell/StateWithholdingNonMandatory/StateWithholdingNonMandatory';

export interface AutoExchangeStepsProps {
  actions;
  autoExchangeAmountLessWithholding: number;
  isLoading: boolean;
  isValid: boolean;
  isValidated: boolean;
  isValidating: boolean;
  paperlessSetting: { paperless; updatedPaperless };
  validationMessages;
  exchangeToExistingLoading: boolean;
  exchangeToExistingLoaded: boolean;
  testNextBtnClicked?: () => void;
  inTest?: boolean;
}

const AutoExchangeSteps: React.VFC<AutoExchangeStepsProps> = ({
  actions: {
    openModal,
    selectExchangeToFunds,
    setProgress,
    submitAutoExchange,
    selectDay,
    selectMonth,
    selectExchangeFromFund,
    selectExchangeToFund,
    updateExchangeFromFundUnit,
    updateExchangeFromFundAmount,
    resetTransaction,
  },
  autoExchangeAmountLessWithholding,
  isLoading,
  isValid,
  isValidated,
  isValidating,
  paperlessSetting: { paperless, updatedPaperless },
  validationMessages,
  exchangeToExistingLoading,
  exchangeToExistingLoaded,
  testNextBtnClicked = () => {},
  inTest,
}) => {
  const dispatch = useDispatch();
  // SELECTORS
  const autoExchangeToSummaryFund: Fund = useSelector(
    getAutoExchangeToSummaryFund
  );
  const autoExchangeFromSummaryFund: Fund = useSelector(
    getAutoExchangeFromSummaryFund
  );
  let currentStep = useSelector(getProgressCurrentStep);

  const [isRepSelected, setIsRepSelected] = useState(false);
  if (inTest) {
    currentStep = 3;
  }
  const {
    grossAmount,
    grossUnit,
    grossAmountDollars,
    closeout = false,
  } = autoExchangeFromSummaryFund;
  const isIFrame: boolean = useSelector(isEmbeddedInIFrame);
  const customerWebViewEnabled: boolean = useSelector(isCustomerWebViewEnabled);
  const isSalesforceRepEnabled: boolean = useSelector(isSalesforceRep);

  const selectedUnit = useSelector(getGrossUnit);
  const exchangeFromIsValid: boolean = useSelector(getExchangeFromIsValid);
  const exchangeToIsValid: boolean = useSelector(getExchangeToIsValid);

  const exchangeFromSummaryFundList = useSelector(
    getExchangeFromSummaryFundList
  );
  const federalWithholding = useSelector(getFederalWithholding);
  const federalWithholdingDollars = useSelector((state: ApplicationState) =>
    getFederalWithholdingDollars(state, grossAmountDollars)
  );
  const autoExchangeFederalWithholdingPercent = federalWithholding.percent;
  const federalWithholdingError =
    autoExchangeFederalWithholdingPercent !== 0 &&
    (autoExchangeFederalWithholdingPercent < FederalTaxPercent.MIN ||
      autoExchangeFederalWithholdingPercent > FederalTaxPercent.MAX);
  const [selectedDays, selectedMonths] = useSelector(getExchangeFrequency);
  const frequencyString = useSelector(getFrequencyString);
  const exchangeIsTaxable: boolean = useSelector(getExchangeIsTaxable);
  const exchangeFundsWithInterestedPartyFlag: boolean = useSelector(
    getExchangeFundsWithInterestedPartyFlag
  );
  const exchangeToSummaryFundList: FundSummary[] = useSelector(
    getExchangeToSummaryFundList
  );
  const hasExchangeFromSweep: boolean = useSelector(getHasExchangeFromSweep);
  const userAddress = useSelector(getUserAddress);
  const autoExchangeStartDate: string = useSelector(getAutoExchangeStartDate);
  const autoExchangeHasEarlyWithdrawalPenalty: boolean = useSelector(
    getAutoExchangeHasEarlyWithdrawalPenalty
  );

  const mandatoryWithholdingStates = useSelector(getMandatoryWithholdingStates);

  const nonMandatoryWithholdingStates = useSelector(
    getNonMandatoryWithholdingStates
  );
  const userStateWithholdingRules = useSelector(getUserStateWithholdingRules);

  const {
    applied: stateWithholdingApplied,
    disabled: stateWithholdingDisabled,
    percent: stateWithholdingPercent,
    isEstimate: stateIsEstimate,
    dollars: stateWithholdingDollars = 0,
  }: Withholding = useSelector((state) =>
    getStateWithholding(state as ApplicationState, {
      closeout,
      grossAmountDollars,
      grossUnit,
    })
  );

  // TODO: move this to redux
  const grossValueString = useMemo(
    () =>
      grossUnit
        ? conditionallyFormatValue({
            grossAmount,
            grossUnit,
          })
        : '',
    [grossAmount, grossUnit]
  );

  // COMPONENTS
  const MaybeSummary = withMaybeFragment(AutoExchangeSummary);
  const MaybeErrorSlideIn = withMaybeFragment(ErrorSlideIn);

  //Edit flow set up
  const exchangeFromFundList = useSelector((state) =>
    getAutoExchangeFromEditFund(state)
  );

  const { electionAgreementUniqueId1, electionAgreementUniqueId2 }: UniqueIDs =
    useSelector(getElectionAgreementUniqueIds);

  useEffect(() => {
    if (isAutoExchangeEdit()) {
      const editFund = exchangeFromFundList[0];
      if (
        exchangeFromFundList[0].currentYearAmount > 0 &&
        exchangeFromFundList[0].currentYearAmountUnit &&
        exchangeFromFundList[0].grossAmount === undefined &&
        exchangeFromFundList[0].grossUnit === undefined
      ) {
        selectExchangeFromFund(editFund.agreementID, editFund);
        updateExchangeFromFundUnit(
          editFund.agreementID,
          CodeToSelectedUnit[editFund.disbursementType?.code]
        );
        updateExchangeFromFundAmount(
          editFund.agreementID,
          editFund.currentYearAmount
        );
        dispatch(
          updateUniqueIDs(
            editFund.electionAgreementUniqueId1,
            editFund.electionAgreementUniqueId2
          )
        );
        selectMonth(exchangeFromFundList[0].transferMonths);
        selectDay(exchangeFromFundList[0].transferDays);
        dispatch(
          updateExchangeFederalWithholdingWithObject({
            federalWithholdingApplied: editFund.IRAWithholding > 0,
            federalWithholdingPercent: editFund.IRAWithholding,
            agreementID: editFund.agreementID,
          })
        );
      }
      if (
        autoExchangeFromSummaryFund.agreementID &&
        !autoExchangeToSummaryFund.agreementID &&
        !exchangeToExistingLoading &&
        !exchangeToExistingLoaded
      ) {
        Promise.all([selectExchangeToFunds(autoExchangeFromSummaryFund)]).then(
          () => {
            selectExchangeToFund(editFund.toFund.agreementID, editFund.toFund);
          }
        );
      }
    }
  });

  const selectedFundsHavePercent = Object.values(
    autoExchangeFromSummaryFund
  )?.some((fund) => fund?.grossUnit === UNIT.PERCENT);
  if (
    selectedFundsHavePercent &&
    window.location.href.indexOf('edit') > -1 &&
    currentStep === 2
  ) {
    dispatch(openModal(ANNUAL_PERCENTAGE_DISCLAIMER));
  }
  const spanTitle = 'step-title';
  const boldedStepWord = 'bolded-step-word';
  return (
    <form className="animated fadeInUp" onSubmit={(e) => e.preventDefault()}>
      <If
        test={
          !(isIFrame || (!customerWebViewEnabled && isSalesforceRepEnabled))
        }
      >
        <AppTitle
          subtitle={
            isAutoExchangeEdit()
              ? ''
              : 'Set up recurring transactions to move assets from one eligible investment to another.'
          }
          title={
            isAutoExchangeEdit()
              ? 'Edit Your Automatic Exchange'
              : 'Automatic Exchange'
          }
        />
      </If>

      {/* STEP 1 */}
      <ProgressStep
        expanded
        active={currentStep === 1}
        className="exchange-from-step step1"
        complete={currentStep > 1}
        nextButtonDisabled={!exchangeFromIsValid}
        nextButtonLabel={
          <span>
            <b>NEXT: </b>Select{' '}
            <span className="exchange-steps-button-exchange-to">
              Exchange To
            </span>{' '}
            Investment
          </span>
        }
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        step={1}
        title={
          <span className={styles[spanTitle]}>
            <span className={styles[boldedStepWord]}>From: </span>
            <span>Select the fund you want to exchange out of</span>
          </span>
        }
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
        onNextClick={() => {
          selectExchangeToFunds(autoExchangeFromSummaryFund);
          setProgress(2);
        }}
      >
        {currentStep === 1 ? (
          <AutoExchangeFromForm
            customerWebViewEnabled={customerWebViewEnabled}
            isSalesforceRep={isSalesforceRepEnabled}
          />
        ) : (
          <MaybeSummary
            suppressChildren
            className="e2e-step-1-collapse"
            handleEditClick={() => setProgress(1)}
            id="exchange-step-1-edit"
            shouldShowFragment={!autoExchangeFromSummaryFund.fundName}
          >
            {`From: ${autoExchangeFromSummaryFund.fundName} ${autoExchangeFromSummaryFund.ticker}, ${grossValueString}`}
          </MaybeSummary>
        )}
      </ProgressStep>

      {/* STEP 2 */}
      <ProgressStep
        expanded
        active={currentStep === 2}
        className="exchange-to-step step2"
        complete={currentStep > 2}
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonDisabled={!exchangeToIsValid || isValidating}
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Set Frequency
          </span>
        }
        nextButtonLoading={isValidating}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        step={2}
        title={
          <span className={styles[spanTitle]}>
            <span className={styles[boldedStepWord]}>To: </span>
            <span>Select the fund you want to exchange into</span>
          </span>
        }
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
        onNextClick={() => setProgress(3)}
      >
        {currentStep === 2 ? (
          <ExchangeToForm
            canAddNewFund={false}
            currentStep={currentStep}
            customerWebViewEnabled={customerWebViewEnabled}
            fromFunds={exchangeFromSummaryFundList}
            isSalesforceRep={isSalesforceRepEnabled}
            selectedFund={autoExchangeToSummaryFund}
            showAddNewFundOrAccount={false}
          />
        ) : (
          <MaybeSummary
            suppressChildren
            className="e2e-step-2-collapse"
            handleEditClick={() => setProgress(2)}
            id="exchange-step-2-edit"
            shouldShowFragment={!autoExchangeToSummaryFund.fundName}
          >
            {`To: ${autoExchangeToSummaryFund.fundName} ${autoExchangeToSummaryFund.ticker}`}
          </MaybeSummary>
        )}
      </ProgressStep>

      {/* STEP 3 */}
      <ProgressStep
        expanded
        active={currentStep === 3}
        className="review-step step3"
        complete={currentStep > 3}
        nextButtonDisabled={
          (isEmpty(selectedDays) || isEmpty(selectedMonths)) && !inTest
        }
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Review
          </span>
        }
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        step={3}
        title={
          <span className={styles[spanTitle]}>
            <span className={styles[boldedStepWord]}>When: </span>
            <span>Choose the frequency of the exchange</span>
          </span>
        }
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
        onNextClick={() => {
          testNextBtnClicked();
          exchangeIsTaxable &&
            openModal(IRA_WITHHOLDING_WARNING_MODAL, {
              // @ts-ignore
              isRoth: exchangeFromSummaryFundList
                .map((fund) => fund.agreementType)
                .some(
                  (fund) =>
                    fund === AGREEMENT_TYPES.ROTH_IRA ||
                    fund === AGREEMENT_TYPES.ROTH_ROLLOVER_IRA
                ),
              mandatoryWithholdingStates,
              userState: autoExchangeFromSummaryFund?.address?.state,
            });
          const startDatePayload = {
            systematicsSelections: [
              {
                agreementID: autoExchangeFromSummaryFund.agreementID,
                days: selectedDays.map(({ value }) => value),
                months: selectedMonths.map(({ value }) => value),
              },
            ],
          };
          dispatch(getRecurringStartDate(startDatePayload)());
          !inTest && setProgress(4);
        }}
      >
        {currentStep === 3 ? (
          <AutoExchangeFrequency
            customerWebViewEnabled={customerWebViewEnabled}
            fromFundName={autoExchangeFromSummaryFund.fundName}
            isSalesforceRep={isSalesforceRepEnabled}
            toFundName={autoExchangeToSummaryFund.fundName}
            {...{
              selectedDays,
              selectedMonths,
              openModal,
              grossValueString,
              grossUnit,
              selectDay,
              selectMonth,
            }}
          />
        ) : (
          <MaybeSummary
            suppressChildren
            className="e2e-step-3-collapse"
            handleEditClick={() => setProgress(3)}
            id="exchange-step-3-edit"
            shouldShowFragment={!frequencyString}
          >
            {frequencyString}
          </MaybeSummary>
        )}
      </ProgressStep>
      <MaybeErrorSlideIn
        show
        msg={validationMessages}
        shouldShowFragment={!isValidated || isValid}
      />

      {/* STEP 4 */}
      <ProgressStep
        active={currentStep === 4}
        checkbox={
          currentStep === 4 &&
          isSalesforceRepEnabled &&
          !customerWebViewEnabled &&
          exchangeIsTaxable ? (
            <div className={styles['availiblity-check-con']}>
              {' '}
              <CheckBox
                checked={isRepSelected}
                inline={true}
                onChange={(e) => setIsRepSelected(e)}
              />
              <span className={styles['availiblity-check-text']}>
                I have read the current{' '}
                <a
                  href="https://home2.troweprice.com/discovery/confluence/display/retailkms/Templates+And+Disclosures#TemplatesAndDisclosures-IRARedemptionDisclosure"
                  rel="noreferrer"
                  target="_blank"
                >
                  Federal Witholding Disclosures
                </a>
                . Note: Disclosure must be read whether federal withholding
                elected or not
              </span>
            </div>
          ) : (
            <></>
          )
        }
        className="review-step step4"
        expanded={currentStep === 4}
        nextButtonDisabled={
          federalWithholdingError ||
          isLoading ||
          (!isRepSelected &&
            isSalesforceRepEnabled &&
            exchangeIsTaxable &&
            !customerWebViewEnabled) ||
          customerWebViewEnabled
        }
        nextButtonLabel="SUBMIT"
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        step={4}
        title={
          <span className={styles[spanTitle]}>
            Review your automatic exchange
          </span>
        }
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
        onNextClick={() => {
          scrollElementIntoFullView('exchange-page-container');

          submitAutoExchange({
            autoExchangeAmount: grossAmount,
            autoExchangeFromSummaryFund,
            autoExchangeToSummaryFund,
            disbursementType: SELECTED_UNIT_ENUM_OBJECT_MAP[selectedUnit],
            exchangeFrequency: { selectedDays, selectedMonths },
            iraWithholding: exchangeIsTaxable ? federalWithholding?.percent : 0,
            paperless: updatedPaperless,
            priorPaperless: getPaperless(paperless, ProductTypes.MUTUALFUND),
            electionAgreementUniqueId1,
            electionAgreementUniqueId2,
          } as AutoExchangePayloadPreSubmit);
          resetTransaction();
        }}
      >
        {currentStep === 4 && (
          <AutoExchangeReview
            fromFund={
              <FundSummaryDisplay
                {...{
                  agreementNumber: autoExchangeFromSummaryFund.agreementNumber,
                  agreementType: autoExchangeFromSummaryFund.agreementType,
                  displayClassification:
                    autoExchangeFromSummaryFund.displayClassification,
                  formattedEntityNames:
                    autoExchangeFromSummaryFund.formattedEntityNames,
                  fundName: autoExchangeFromSummaryFund.fundName,
                  ticker: autoExchangeFromSummaryFund.ticker,
                }}
                {...autoExchangeFromSummaryFund}
              />
            }
            toFund={
              <FundSummaryDisplay
                {...{
                  agreementNumber: autoExchangeToSummaryFund.agreementNumber,
                  agreementType: autoExchangeToSummaryFund.agreementType,
                  displayClassification:
                    autoExchangeToSummaryFund.displayClassification,
                  formattedEntityNames:
                    autoExchangeToSummaryFund.formattedEntityNames,
                  fundName: autoExchangeToSummaryFund.fundName,
                  ticker: autoExchangeToSummaryFund.ticker,
                }}
                {...autoExchangeToSummaryFund}
              />
            }
            {...{
              startDate: autoExchangeStartDate,
              exchangeFundsWithInterestedPartyFlag,
              exchangeIsTaxable,
              exchangeToSummaryFundList,
              frequencyString,
              grossValueString,
              hasExchangeFromSweep,
              userAddress,
              openModal,
              grossUnit,
            }}
          >
            {/* Show children if the exchange is taxable */}
            {exchangeIsTaxable && (
              <span>
                <WithholdingPanel
                  showWithholdingWarning={autoExchangeHasEarlyWithdrawalPenalty}
                >
                  <DeductionList
                    showTransactionFees
                    customerWebViewEnabled={customerWebViewEnabled}
                    isSalesforceRep={isSalesforceRepEnabled}
                    showRedemptionAmountMessage={[
                      UNIT.PERCENT,
                      UNIT.SHARES,
                    ].includes(selectedUnit)}
                    {...{
                      federalWithholdingApplied: federalWithholding.applied,
                      federalWithholdingDollars: federalWithholdingDollars,
                      federalWithholdingPercent: federalWithholding?.percent,
                      isRetirementLike:
                        !autoExchangeFromSummaryFund?.nonRetirementLike,
                      mandatoryWithholdingStates,
                      grossAmountDollars,
                      stateIsEstimate,
                      stateWithholdingApplied,
                      stateWithholdingDisabled,
                      stateWithholdingDollars,
                      stateWithholdingPercent,
                      userState: userAddress?.state,
                      withholdingEditAction:
                        updateExchangeFederalWithholdingWithObject,
                    }}
                    fund={{
                      ...autoExchangeFromSummaryFund,
                      netDollarAmount: autoExchangeAmountLessWithholding,
                    }}
                  >
                    Take-Home Amount
                  </DeductionList>
                </WithholdingPanel>

                {!customerWebViewEnabled && isSalesforceRepEnabled && (
                  <span>
                    <If test={!autoExchangeFromSummaryFund?.nonRetirementLike}>
                      <div
                        className={
                          !customerWebViewEnabled && isSalesforceRepEnabled
                            ? 'market-volatility-note-box'
                            : ''
                        }
                      >
                        <div
                          className={
                            !customerWebViewEnabled && isSalesforceRepEnabled
                              ? 'market-volatility-note'
                              : ''
                          }
                        >
                          <IraWithholdingWarning
                            caller={null}
                            closeIraWithholdingWarning={null}
                            isSalesforceRepMessage={true}
                            setInputFocus={null}
                          />
                        </div>
                      </div>
                    </If>
                    <If test={!autoExchangeFromSummaryFund?.nonRetirementLike}>
                      <div
                        className={
                          !customerWebViewEnabled && isSalesforceRepEnabled
                            ? 'market-volatility-note-box'
                            : ''
                        }
                      >
                        <div
                          className={
                            !customerWebViewEnabled && isSalesforceRepEnabled
                              ? 'market-volatility-note'
                              : ''
                          }
                        >
                          {mandatoryWithholdingStates.includes(
                            autoExchangeFromSummaryFund?.address?.state
                          ) ? (
                            <StateIncomeTaxDisclosure
                              closeStateIncomeTaxDisclosure={null}
                              isSalesforceRepMessage={true}
                              mandatoryWithholdingStates={
                                mandatoryWithholdingStates
                              }
                              nonMandatoryWithholdingStates={
                                nonMandatoryWithholdingStates
                              }
                              state={
                                autoExchangeFromSummaryFund?.address?.state
                              }
                              stateWithholdingRules={userStateWithholdingRules}
                            />
                          ) : (
                            <StateWithholdingNonMandatory
                              isSalesforceRepMessage={true}
                              state={
                                autoExchangeFromSummaryFund?.address?.state
                              }
                            />
                          )}
                        </div>
                      </div>
                    </If>
                  </span>
                )}
              </span>
            )}
          </AutoExchangeReview>
        )}
      </ProgressStep>
    </form>
  );
};

const mapStateToProps = (state: ApplicationState) => {
  const {
    executeExchange: {
      isLoading,
      isValid,
      isValidated,
      isValidating,
      validationMessages,
    },
    paperlessSetting,
    exchangeTransaction: {
      exchangeToExistingLoading,
      exchangeToExistingLoaded,
    },
  } = state;
  return {
    autoExchangeAmountLessWithholding:
      getAutoExchangeAmountLessWithholding(state),
    isIFrame: isEmbeddedInIFrame(state),
    isSalesforceRep: isSalesforceRep(state),
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    isLoading,
    isValid,
    isValidated,
    isValidating,
    paperlessSetting,
    validationMessages,
    exchangeToExistingLoading,
    exchangeToExistingLoaded,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    ...bindActionCreators(moduleActions, dispatch),
    setProgress(step: number) {
      scrollElementIntoFullView(`step${step}`);
      dispatch(moduleActions.setProgress(step) as Action);
    },
    selectExchangeToFunds(fund: Fund) {
      if (hasValue(fund)) {
        // POST selected fund
        dispatch(moduleActions.searchExchangeToExisting(fund) as Action);
      }
    },
  },
  selectDay: moduleActions.selectDay,
  selectMonth: moduleActions.selectMonth,
});

export default connect(mapStateToProps, mapDispatchToProps)(AutoExchangeSteps);
